/***********************/
/* Angular + external */
import {
  Component,
  forwardRef,
  ViewChild,
  Injector,
  ChangeDetectionStrategy,
  Injectable,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ObjectUtils } from "@bfx/primeng/utils";

// models

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ICDBase, ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ICD_CLOCK_TIME_PATTERN } from "../../services/atdl/atdl-utility.service";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
import { ICDClockModel } from "../../data-idc/controls/icd-clock.model";
import { InputMask } from "@bfx/primeng/inputmask";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";
import { EmphasisMixin } from "../../../../shared/base-components/mixins/emphasis";
import { FieldStateMixin } from "../../../../shared/base-components/mixins/field-state";
import { InputtedMixin } from "../../../../shared/base-components/mixins/inputted";
import { InputLabelStateMixin } from "../../../../shared/base-components/mixins/label-state";
import { PlaceholderMixin } from "../../../../shared/base-components/mixins/placeholder";
import { SizeMixin } from "../../../../shared/base-components/mixins/size";

export const ICD_CLOCK_DEFAULT: string = "00:00:00";
export const ICD_CLOCK_MASK: string = "99:99:99";

export const CLOCK_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICDClockComponent),
  multi: true,
};

@Injectable()
class ICDClock extends ICDBase<ICDClockModel> implements ControlValueAccessor {
  /**********************************************************************/
  /* DATA */
  public config: any = {
    maxValue: null,
    minValue: null,
    mask: ICD_CLOCK_MASK,
  };

  public subData: any = {
    timeValue: null, // internal control model -> string
    defaultValue: null,
    initialValue: null,
    emptyValue: ICD_CLOCK_DEFAULT,
  };

  /************************************/
  onModelChange: Function = () => {};
  onModelTouched: Function = () => {};

  /************************************/
  /* Element Ref */
  @ViewChild("inputField", { static: true }) inputField: InputMask;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZE */

  protected initialize(): void {
    super.initialize();
    this.label = "Clock";
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API  */

  protected initializeICDSpecificModelProperties(): boolean {
    let error: boolean = false;
    this.cd.markForCheck();
    if (this.model instanceof ATDLInterfaceControlModel) {
      if (this.model.validity != null) {
        // maxValue
        if (
          this.model.validity.hasOwnProperty("maxValue") &&
          typeof this.model.validity.maxValue === "string" &&
          ICD_CLOCK_TIME_PATTERN.test(this.model.validity.maxValue)
        ) {
          this.config.maxValue = this.model.validity.maxValue;
        }
        // minValue
        if (
          this.model.validity.hasOwnProperty("minValue") &&
          typeof this.model.validity.minValue === "string" &&
          ICD_CLOCK_TIME_PATTERN.test(this.model.validity.minValue)
        ) {
          if (
            this.config.maxValue != null &&
            this.atdlUtility.compareTimeGreaterOrEqualThan(
              this.model.validity.minValue,
              this.config.maxValue,
            )
          ) {
            this.config.minValue = null;
          } else {
            this.config.emptyValue = this.config.minValue = this.model.validity.minValue;
          }
        }
      }

      // INIT VALUE
      if (typeof this.model.initValue === "string") {
        this.setAndUpdateValue(this.model.initValue);
      }

      // DEFAULT VALUE
      if (typeof this.model.defaultValue === "string") {
        this.data.defaultValue = this.model.defaultValue;
      } else {
        this.data.defaultValue = this.data.emptyValue;
      }
    } else {
      error = true;
    }
    return error;
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  protected handleConstraintUpdate(data: any): void {
    this.handleDynResponses(this.processExternalStateFlowDependency(data, new Map()));
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    this.data.value = value;
    this.model.value = this.data.value;
    this.inputLabelState = this.locked || this.disabled || value.disabled ? "disabled" : "valid";
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */

  public onFocus(event: any): void {
    this.cd.markForCheck();
    this.inputFocused = true;
    this.dirty = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.data.errorMsg = "";
  }

  public onBlur(event: any): void {
    let dynResponses = new Map();
    this.inputFocused = false;
    this.updateValue();
    this.validate();
    dynResponses = this.processAuxStateFlow(dynResponses);
    dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
    this.handleDynResponses(dynResponses);
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  public focusInput() {
    setTimeout(() => this.inputField?.el?.nativeElement.focus(), 50);
  }

  /**********************************************************************/
  /* EXTERNAL API */

  public setDefault(): void {
    this.cd.markForCheck();
    this.setAndUpdateValue(this.data.defaultValue);
  }

  protected setAndUpdateValue(value: any): void {
    this.cd.markForCheck();
    if (value == null) {
      this.set(null);
      this.updateValue();
    } else if (typeof value === "string" || value instanceof ICDClockModel) {
      this.set(value);
      this.updateValue();
    } else {
      logger.out.error(`ERROR: ${this.constructor.name}.setAndUpdateValue(): Invalid value`);
    }
  }

  public updateValue(): void {
    this.cd.markForCheck();
    this.fillInTheBlanks();
    this.data.defaultValue =
      typeof this.data.timeValue === "string" ? this.data.timeValue : this.data.defaultValue;
    this.data.value = this.compose();
    this.writeValue(this.data.value);
  }

  public load(value: ICDClockModel, propagate?: boolean): void {
    let dynResponses = new Map();
    if (value instanceof ICDClockModel || value === null) {
      this.cd.markForCheck();

      this.set(value);
      this.updateValue();
      this.validate();

      dynResponses.set(ICDNResponseCommand.LOAD_COMPLETE, null);

      dynResponses = this.processAuxStateFlow(dynResponses);
      if (typeof propagate === "boolean" && propagate) {
        dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }
    }
    this.handleDynResponses(dynResponses);
  }

  /**
   * The set() method is used in order to assign value to the internal(displayed) control model ('this.data.timeValue') as well as
   * the auxCheckbox when proper type (ICDTextFieldModel) is provided.
   */
  public set(value: ICDClockModel | string): void {
    let dynResponses = new Map();

    if (value instanceof ICDClockModel || typeof value === "string" || value == null) {
      this.cd.markForCheck();
      this.dirty = true;
      if (value instanceof ICDClockModel && value.auxModel instanceof ICDAuxCheckboxModel) {
        this.data.value = value;
        if (value.auxModel.enabled) {
          this.auxCheckbox.enabled = value.auxModel.enabled;
          this.auxCheckbox.mode = value.auxModel.mode;
          if (value.auxModel.mode === ICDNAuxMode.DISABLED) {
            // value assignment
            this.data.timeValue = "";
            // checkbox assignment
            this.auxCheckbox.value = false;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.REQUIRED) {
            // value assignment
            this.data.timeValue = value.value;
            // checkbox assignment
            this.auxCheckbox.value = true;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.OPTIONAL) {
            this.data.timeValue = value.value;
            //
            this.auxCheckbox.value = value.auxModel.value;
            this.auxCheckbox.locked = false;
          } else {
            logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid auxMode`);
          }
        } else {
          this.data.timeValue = value.value;
          //
          this.auxCheckbox.enabled = false;
          this.auxCheckbox.mode = ICDNAuxMode.DISABLED;
          this.auxCheckbox.value = false;
          this.auxCheckbox.locked = true;
        }
        dynResponses = this.processAuxStateFlow(dynResponses);
        dynResponses = this.processICDInternalStateFlow(dynResponses);
      } else if (value instanceof ICDClockModel && value.auxModel === null) {
        this.data.timeValue = value.value;
      } else if (typeof value === "string") {
        this.data.timeValue = value;
      } else if (value == null) {
        this.data.timeValue = "";
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid value type`);
      }
    }
    this.handleDynResponses(dynResponses);
  }

  /**
   * The compose() method is used in order to generate an outgoing model for the control (in this case ICDTextFieldModel)
   * which will be assigned to the main model 'this.data.value' and propagated to the panel.
   */
  private compose(): ICDClockModel {
    let model = new ICDClockModel();
    let auxModel = new ICDAuxCheckboxModel();
    if (this.auxCheckbox.enabled) {
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.enabled = this.auxCheckbox.enabled;
      auxModel.mode = this.auxCheckbox.mode;
      auxModel.value = this.auxCheckbox.value;
      if (this.auxCheckbox.mode === ICDNAuxMode.DISABLED) {
        model.value = null;
      } else if (this.auxCheckbox.mode === ICDNAuxMode.REQUIRED) {
        model.value =
          typeof this.data.timeValue === "string" && this.data.timeValue.length > 0
            ? this.data.timeValue
            : null;
      } else if (this.auxCheckbox.mode === ICDNAuxMode.OPTIONAL) {
        model.value = this.auxCheckbox.value ? this.data.timeValue : null;
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid auxMode`);
        model.value =
          typeof this.data.timeValue === "string" && this.data.timeValue.length > 0
            ? this.data.timeValue
            : null;
      }
    } else {
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.enabled = this.auxCheckbox.enabled;
      auxModel.mode = this.auxCheckbox.mode;
      //
      model.value =
        typeof this.data.timeValue === "string" && this.data.timeValue.length > 0
          ? this.data.timeValue
          : null;
    }
    model.nulledByDependency = this.data?.value?.nulledByDependency ?? false;
    model.disabled = this.disabled || this.data.disabled || !this.visible;
    model.auxModel = auxModel;
    return model;
  }

  public validateIC(): boolean {
    this.cd.markForCheck();
    // Don't want to use timeValue for representing null at the moment since it seems
    // to be displayed directly in control - may require futher investigation
    const isNullValue = this.data?.value?.nulledByDependency ?? false;
    const validate =
      this.required ||
      !(this.locked || (this.disabled && this.auxCheckbox.enabled == false) || isNullValue);

    if (this.data != null && validate) {
      // Time validation
      if (ICD_CLOCK_TIME_PATTERN.test(this.data.timeValue)) {
        if (
          this.config.maxValue != null &&
          !this.atdlUtility.compareTimeGreaterOrEqualThan(this.config.maxValue, this.data.timeValue)
        ) {
          this.data.state = ICNInputControlState.INVALID;
          this.data.errorMsg = `Time greater than max ${this.config.maxValue}`;
          return false;
        } else if (
          this.config.minValue != null &&
          !this.atdlUtility.compareTimeGreaterOrEqualThan(this.data.timeValue, this.config.minValue)
        ) {
          this.data.state = ICNInputControlState.INVALID;
          this.data.errorMsg = `Time less than min ${this.config.minValue}`;
          return false;
        } else {
          this.data.state = ICNInputControlState.VALID;
          this.data.errorMsg = "";
          return true;
        }
      } else {
        this.data.state = ICNInputControlState.INVALID;
        this.data.errorMsg = "Invalid time format";
        return false;
      }
    } else if (validate) {
      this.data.state = ICNInputControlState.INVALID;
      this.data.errorMsg = `${this.label} must not be empty`;
      return false;
    } else {
      this.data.state = ICNInputControlState.INACTIVE;
      this.data.errorMsg = "";
    }
    return true;
  }

  public resetIC(): void {
    this.cd.markForCheck();
    // Internal model
    this.data.timeValue = "";
    this.data.initValue = null;
    this.data.defaultValue = null;
    this.data.emptyValue = ICD_CLOCK_DEFAULT;
    // Label
    this.label = "Clock";
    // Reset config
    this.config.minValue = null;
    this.config.maxValue = null;
  }

  protected fillInTheBlanks() {
    this.cd.markForCheck();
    if (this.data.timeValue == null || this.data.timeValue === "") {
      this.data.timeValue = this.data.emptyValue;
    } else if (this.data.timeValue != null && this.data.timeValue.length > 0) {
      this.data.timeValue = this.data.timeValue.replace(/_/g, "0");
    }
  }
}

const Mixins = EmphasisMixin(
  SizeMixin(FieldStateMixin(InputLabelStateMixin(InputtedMixin(PlaceholderMixin(ICDClock))))),
);

@Component({
  selector: "icd-clock",
  host: {
    ...ICD_BASE_DEFAULT_HOST_RECORD,
    "[class.icd__clock__container]": "true",
  },
  templateUrl: "./html/icd-clock.html",
  providers: [CLOCK_VALUE_ACCESSOR, ObjectUtils],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [BfxInputDirective],
  standalone: false,
})
export class ICDClockComponent extends Mixins {
  public inputDirective: BfxInputDirective;

  constructor(injector: Injector) {
    super(injector);
    this.inputDirective = this.injector.get(BfxInputDirective);
    this.size = "sm";
    this.emphasis = "bold";
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }
}
